export type VitalSign = {
  id: string;
  label: string;
  value: string;
  unit?: string;
};

export type SystemFinding = {
  id: string;
  label: string;
  value: string;
};

export type ExamSystem = {
  id: string;
  name: string;
  findings: SystemFinding[];
};

export type ToolDescriptor = {
  id: string;
  name: string;
  description: string;
};

export type BodyRegion = {
  id: string;
  label: string;
};

export type RegionNotes = Record<string, string>;

export const examTools: ToolDescriptor[] = [
  {
    id: "inspect",
    name: "Inspection",
    description: "Visual assessment of surface findings and symmetry.",
  },
  {
    id: "palpate",
    name: "Palpation",
    description: "Assess temperature, tenderness, and texture by touch.",
  },
  {
    id: "percuss",
    name: "Percussion",
    description: "Evaluate underlying structures through percussion tones.",
  },
  {
    id: "auscultate",
    name: "Auscultation",
    description: "Listen for physiologic sounds with a stethoscope.",
  },
  {
    id: "neuro",
    name: "Neurological",
    description: "Test reflexes, tone, power, and higher cortical functions.",
  },
];

export const examVitalSigns: VitalSign[] = [
  { id: "temp", label: "Temperature", value: "40", unit: "deg C" },
  { id: "hr", label: "Heart Rate", value: "140", unit: "bpm" },
  { id: "bp", label: "Blood Pressure", value: "80/50", unit: "mmHg" },
  { id: "sao2", label: "SaO2", value: "", unit: "%" },
  { id: "rr", label: "Respiratory Rate", value: "", unit: "breaths/min" },
];

export const examSystems: ExamSystem[] = [
  {
    id: "general",
    name: "General",
    findings: [
      { id: "pallor", label: "Pallor", value: "No" },
      { id: "jaundice", label: "Jaundice", value: "No" },
      { id: "handSigns", label: "Hand Signs", value: "No" },
      { id: "edema", label: "Lower Limb Edema", value: "No" },
      { id: "skin", label: "Skin", value: "Cold and clammy" },
      { id: "scar", label: "Scar", value: "No" },
    ],
  },
  {
    id: "cardiovascular",
    name: "Cardiovascular",
    findings: [
      { id: "jvp", label: "JVP", value: "Not elevated" },
      { id: "apex", label: "Cardiac Apex", value: "Central" },
      { id: "heartSounds", label: "Heart Sounds", value: "Normal" },
      { id: "murmur", label: "Murmur", value: "No" },
    ],
  },
  {
    id: "respiratory",
    name: "Respiratory",
    findings: [
      { id: "movement", label: "Chest Movement", value: "Symmetrical" },
      { id: "breathSound", label: "Breath Sound", value: "Vesicular" },
      { id: "addedSound", label: "Added Sound", value: "No" },
    ],
  },
  {
    id: "abdomen",
    name: "Abdomen",
    findings: [
      { id: "tenderness", label: "Tenderness", value: "No" },
      { id: "mass", label: "Mass", value: "No" },
      { id: "organomegaly", label: "Organomegaly", value: "No" },
      { id: "bowel", label: "Bowel Sound", value: "Present" },
    ],
  },
  {
    id: "neurological",
    name: "Neurological",
    findings: [
      { id: "gcs", label: "GCS", value: "13 (E4 V4 M5)" },
      { id: "tone", label: "Muscle Tone", value: "Normal" },
      { id: "power", label: "Power", value: "5/5 right, 5/5 left" },
      { id: "sensory", label: "Sensory", value: "Intact" },
      { id: "speech", label: "Speech", value: "Inappropriate words" },
    ],
  },
  {
    id: "neck",
    name: "Neck",
    findings: [
      { id: "rigidity", label: "Neck Rigidity", value: "Mild" },
      { id: "vesicles", label: "Vesicular Lesions", value: "Absent" },
    ],
  },
];

export const examNarrative =
  "On admission Jenny was febrile and confused with cold clammy limbs. Temperature 40 deg C, heart rate 140 per minute, blood pressure 80/50 mmHg.";

export const examBodyRegions: BodyRegion[] = [
  { id: "head", label: "Head & Neck" },
  { id: "chest", label: "Thorax" },
  { id: "abdomen", label: "Abdomen" },
  { id: "arms", label: "Upper Limbs" },
  { id: "legs", label: "Lower Limbs" },
  { id: "back", label: "Back" },
  { id: "neuro", label: "Neurological" },
];

export const examRegionNotes: RegionNotes = {
  head:
    "No vesicular lesions. Mild neck rigidity. Child confused with appropriate eye opening and localization of pain.",
  chest: "Vesicular breath sounds. No added sounds.",
  abdomen: "Soft, non tender, no organomegaly.",
  neck: "Mild rigidity on palpation.",
};

export const examRegionSystemMap: Record<string, string[]> = {
  head: ["General", "Neurological", "Neck"],
  chest: ["Respiratory", "Cardiovascular"],
  abdomen: ["Abdomen"],
  arms: ["General"],
  legs: ["General"],
  back: ["Respiratory"],
  neuro: ["Neurological"],
};
